import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity, Heart, Thermometer, Wind, Droplets, Weight, User, Clock,
  AlertTriangle, RefreshCw, Check, X, Search, Stethoscope, Volume2, RotateCcw,
  SkipForward, Edit2, ClipboardCheck, ListChecks, Syringe, Phone,
} from 'lucide-react';
import { visitAPI, treatmentCourseAPI, serverFetch } from '../services/api';
import { useToast, useConfirm, usePrompt } from './UIProvider';
import useDialog from '../hooks/useDialog';
import Select from './Select';
import DeferVisitModal from './DeferVisitModal';
import { formatClinicDate, formatClinicTime } from '../utils/clinicTime';
import { ageInYears } from '../utils/age';
import { scrollContentToTop } from '../utils/scroll';
import { toTitleCase } from '../utils/text';
import { computeBmi, getAgeBandKey } from '../utils/vitals';
// Tokens are sequenced per doctor per day, so the per-doctor prefix is what makes one doctor's
// "1" distinct from another's. Nurse Station was printing the bare number while the token receipt
// the patient is holding says "FB-1", so the two did not match.
import { formatVisitToken } from './TokenPrintModal';

/**
 * Nurse Station — staff records vitals for patients in the PENDING_VITALS queue. Once recorded,
 * the patient moves to READY_FOR_DOCTOR and appears in the doctor's queue.
 *
 * Mandatory: BP (systolic + diastolic) and Pulse. Everything else is optional.
 */
const NurseStation = ({ authUser, hasPermission }) => {
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('NURSE_STATION', action) : true),
    [hasPermission],
  );
  const notify = useToast();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // With several doctors running a nurse, the queue is one shared arrival-ordered list. This lets
  // the nurse narrow it to one doctor without losing that ordering.
  const [filterDoctor, setFilterDoctor] = useState('all');
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [deferVisit, setDeferVisit] = useState(null); // visit currently being deferred (modal open if non-null)
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'recorded'
  const [recorded, setRecorded] = useState([]); // visits whose vitals were recorded today
  const [recordedLoading, setRecordedLoading] = useState(false);
  // Injections and drips given here one dose per visit: doses paid for today and waiting to be
  // given, doses given today, and courses the patient has fallen behind on.
  const [injections, setInjections] = useState({ toGive: [], givenToday: [], behind: [] });
  const [injectionsLoading, setInjectionsLoading] = useState(false);
  const [recording, setRecording] = useState(null); // the dose or visit being recorded
  const prompt = usePrompt();

  const loadQueue = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await visitAPI.getNurseQueue();
      setQueue(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load nurse queue:', err);
      setError('Failed to load patients waiting for vitals');
      setQueue([]);
    } finally { setLoading(false); }
  }, []);

  const loadRecorded = useCallback(async () => {
    setRecordedLoading(true);
    try {
      const data = await visitAPI.getRecordedToday();
      setRecorded(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load recorded-today list:', err);
      setRecorded([]);
    } finally { setRecordedLoading(false); }
  }, []);

  const canSeeInjections = can('VIEW_INJECTIONS_TAB');
  const loadInjections = useCallback(async () => {
    if (!canSeeInjections) return;
    setInjectionsLoading(true);
    try {
      const data = await treatmentCourseAPI.today();
      setInjections({
        toGive: data?.toGive || [],
        givenToday: data?.givenToday || [],
        behind: data?.behind || [],
      });
    } catch (err) {
      console.error('Failed to load injections:', err);
    } finally { setInjectionsLoading(false); }
  }, [canSeeInjections]);

  useEffect(() => { loadQueue(); loadRecorded(); loadInjections(); }, [loadQueue, loadRecorded, loadInjections]);

  // Switching the primary tab swaps the whole page content — reset scroll so the new
  // tab doesn't inherit the previous one's scroll position.
  useEffect(() => { scrollContentToTop(); }, [activeTab]);

  // Auto-refresh every 15 seconds so new tokens appear without manual refresh.
  // Every list refreshes so the badge counts stay current, including doses just paid for.
  useEffect(() => {
    const interval = setInterval(() => { loadQueue(); loadRecorded(); loadInjections(); }, 15000);
    return () => clearInterval(interval);
  }, [loadQueue, loadRecorded, loadInjections]);

  /** Records doses as given (one, or every dose of a visit), then refreshes the list. */
  const recordGiven = async (doseIds, key) => {
    if (recording) return;
    setRecording(key);
    try {
      await treatmentCourseAPI.markGiven(doseIds);
      notify('success', doseIds.length === 1 ? 'Dose recorded as given' : `${doseIds.length} doses recorded as given`);
      await loadInjections();
    } catch (err) {
      notify('error', err?.message || 'Could not record the dose');
    } finally { setRecording(null); }
  };

  /** Ends a course early, with the reason the nurse gives. */
  const stopCourse = async (row) => {
    const reason = await prompt({
      title: 'Stop this course?',
      subtitle: `${row.patientName} · ${row.prescriptionNumber}`,
      placeholder: 'e.g. The doctor stopped it, the patient refused, a reaction',
      multiline: true,
      required: true,
      errorMessage: 'Say why the course is being stopped',
      confirmText: 'Stop course',
      tone: 'danger',
    });
    if (!reason) return;
    try {
      await treatmentCourseAPI.stop(row.courseId, reason);
      notify('success', 'Course stopped');
      await loadInjections();
    } catch (err) {
      notify('error', err?.message || 'Could not stop the course');
    }
  };

  // Shared filter — applied to whichever list is active in the current tab
  const matchesSearch = (v) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    const name = `${v.patient?.firstName || ''} ${v.patient?.lastName || ''}`.toLowerCase();
    return (
      name.includes(q) ||
      (v.patient?.mrn || '').toLowerCase().includes(q) ||
      String(v.tokenNumber || '').includes(q) ||
      // Also match the prefixed form, so typing the token exactly as printed ("FB-1") finds it.
      formatVisitToken(v).toLowerCase().includes(q)
    );
  };
  const matchesDoctor = (v) => filterDoctor === 'all' || v.doctor?.id === filterDoctor;
  // Says which narrowing actually emptied the list — a doctor filter with no search text used to
  // report: No matches for "".
  const emptyReason = (base) => {
    const doctorName = doctorOptions.find(o => o.value === filterDoctor)?.label;
    if (searchTerm && doctorName) return `No matches for "${searchTerm}" under ${doctorName}`;
    if (searchTerm) return `No matches for "${searchTerm}"`;
    if (doctorName) return `${base} for ${doctorName}`;
    return base;
  };
  const matches = (v) => matchesSearch(v) && matchesDoctor(v);
  const filtered = queue.filter(matches);
  const filteredRecorded = recorded.filter(matches);

  // Options come from the visits actually in front of the nurse today (both tabs), so the dropdown
  // only ever offers doctors she has patients for, and needs no extra request. Union of both lists
  // keeps the options steady when switching tabs.
  const doctorOptions = (() => {
    const byId = new Map();
    for (const v of [...queue, ...recorded]) {
      if (v.doctor?.id && !byId.has(v.doctor.id)) {
        byId.set(v.doctor.id, v.doctor.fullName
          || `${v.doctor.firstName || ''} ${v.doctor.lastName || ''}`.trim()
          || 'Unknown');
      }
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  })();

  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

  const handleNurseCall = async (visitId) => {
    // The endpoint echoes back a bare token number; the prefix lives on the visit, so read it from
    // the queue we already have rather than showing the nurse a token the patient won't recognise.
    const called = queue.find(v => v.id === visitId);
    try {
      const response = await serverFetch(`${apiBase}/token-calls/${visitId}/nurse-call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('clinic_auth_token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        const shown = called ? formatVisitToken(called) : data.tokenNumber;
        notify('success', `Token ${shown} called for vitals - ${data.patientName}`);
        // Optimistic flip: stamp the visit locally so the button immediately switches to Recall
        // without waiting for the next loadQueue() round-trip.
        setQueue(prev => prev.map(v => v.id === visitId ? { ...v, nurseCalledAt: new Date().toISOString() } : v));
      } else {
        notify('error', data.message || 'Failed to call patient');
      }
    } catch (err) {
      notify('error', 'Failed to call patient');
    }
  };

  const handleNurseRecall = async (visitId) => {
    try {
      const response = await serverFetch(`${apiBase}/token-calls/${visitId}/nurse-recall`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('clinic_auth_token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        notify('info', 'Patient recalled for vitals - announcement sent to Display Board');
        // Refresh the timestamp so a subsequent hover/tooltip shows the latest call time
        setQueue(prev => prev.map(v => v.id === visitId ? { ...v, nurseCalledAt: new Date().toISOString() } : v));
      } else {
        notify('error', data.message || 'Failed to recall patient');
      }
    } catch (err) {
      notify('error', 'Failed to recall patient');
    }
  };

  const handleDefer = async (visitId, afterVisitId) => {
    try {
      const response = await serverFetch(`${apiBase}/visits/${visitId}/defer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('clinic_auth_token')}`,
        },
        body: JSON.stringify({ afterVisitId: afterVisitId || null }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to defer patient');
      }
      notify('success', afterVisitId ? 'Patient moved behind selected position' : 'Patient moved to end of queue');
      setDeferVisit(null);
      loadQueue();
    } catch (err) {
      notify('error', err.message || 'Failed to defer patient');
    }
  };

  const handleVitalsRecorded = (updatedVisit) => {
    notify('success', `Vitals recorded for ${updatedVisit.patient?.firstName} ${updatedVisit.patient?.lastName}`);
    setSelectedVisit(null);
    loadQueue();
    loadRecorded(); // so the entry appears in Recorded Today (or its values update if amending)
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
                <Heart className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Nurse Station</h1>
                <p className="text-gray-600">Record vitals for patients before they see the doctor</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-xl">
                <div className="text-xs text-emerald-600 font-medium">Waiting</div>
                <div className="text-2xl font-bold text-emerald-800">{queue.length}</div>
              </div>
              <button
                onClick={loadQueue}
                className="p-3 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl shadow-xl p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by patient name, MR#, or token number..."
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            {/* Only shown once more than one doctor has patients here — with a single doctor it
                would be a control with nothing to choose. */}
            {doctorOptions.length > 1 && (
              <div className="sm:w-64 flex-none">
                <Select
                  value={filterDoctor}
                  onChange={setFilterDoctor}
                  options={[{ value: 'all', label: `All Doctors (${queue.length})` }, ...doctorOptions]}
                  buttonClassName="px-4 py-3 rounded-xl"
                />
              </div>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* Tabs — Pending vs Recorded Today. Pill-style with strong active fill so the
            existence of a second view is immediately obvious to the nurse. Each tab is
            gated by its own VIEW_*_TAB permission. */}
        <div className="flex gap-2 mb-4">
          {[
            { key: 'pending',  label: 'Pending Vitals',  icon: ListChecks,     count: queue.length,    perm: 'VIEW_PENDING_TAB' },
            { key: 'recorded', label: 'Recorded Today',  icon: ClipboardCheck, count: recorded.length, perm: 'VIEW_RECORDED_TAB' },
            { key: 'injections', label: 'Injections', icon: Syringe,
              count: injections.toGive.reduce((n, v) => n + (v.doses || []).length, 0), perm: 'VIEW_INJECTIONS_TAB' },
          ].filter(t => can(t.perm)).map(t => {
            const Icon = t.icon;
            const isActive = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  isActive
                    ? 'bg-navy-800 text-white shadow-md shadow-navy-200/60'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow-sm'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{t.label}</span>
                {t.count > 0 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                    isActive ? 'bg-white/25 text-white' : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Pending-Vitals tab content (the original queue) */}
        {activeTab === 'pending' && can('VIEW_PENDING_TAB') && (
          loading && queue.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
              <RefreshCw className="h-10 w-10 text-gray-300 mx-auto mb-3 animate-spin" />
              <p className="text-gray-500">Loading patients…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-10 w-10 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-1">
                {queue.length === 0 ? 'No patients waiting for vitals' : emptyReason('No patients waiting for vitals')}
              </h3>
              <p className="text-gray-500">
                {queue.length === 0
                  ? 'New tokens will appear here automatically.'
                  : 'Try a different search term, or clear the search.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((visit) => (
                <NurseQueueCard
                  key={visit.id}
                  visit={visit}
                  onRecordVitals={() => setSelectedVisit(visit)}
                  onCall={() => handleNurseCall(visit.id)}
                  onRecall={() => handleNurseRecall(visit.id)}
                  onDefer={() => setDeferVisit(visit)}
                  can={can}
                />
              ))}
            </div>
          )
        )}

        {/* Recorded-Today tab content — review/amend recorded vitals */}
        {activeTab === 'recorded' && can('VIEW_RECORDED_TAB') && (
          recordedLoading && recorded.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
              <RefreshCw className="h-10 w-10 text-gray-300 mx-auto mb-3 animate-spin" />
              <p className="text-gray-500">Loading recorded vitals…</p>
            </div>
          ) : filteredRecorded.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClipboardCheck className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-1">
                {recorded.length === 0 ? 'No vitals recorded yet today' : emptyReason('No vitals recorded yet today')}
              </h3>
              <p className="text-gray-500">
                {recorded.length === 0
                  ? 'Vitals you record will appear here so you can review or amend them.'
                  : 'Try a different search term, or clear the search.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredRecorded.map((visit) => (
                <RecordedVitalsCard
                  key={visit.id}
                  visit={visit}
                  onEdit={() => setSelectedVisit(visit)}
                />
              ))}
            </div>
          )
        )}

        {/* Injections tab: injections and drips given here one dose per visit. Doses the counter took
            payment for today, what has been given, and courses the patient has fallen behind on. */}
        {activeTab === 'injections' && canSeeInjections && (
          <InjectionsPanel
            data={injections}
            loading={injectionsLoading}
            search={searchTerm}
            canRecord={can('RECORD_DOSE')}
            recording={recording}
            onGiven={recordGiven}
            onStop={stopCourse}
          />
        )}

        {/* Vitals Modal */}
        {selectedVisit && (
          <VitalsModal
            visit={selectedVisit}
            recordedBy={authUser?.id}
            onClose={() => setSelectedVisit(null)}
            onSaved={handleVitalsRecorded}
          />
        )}

        {/* Defer Modal */}
        {deferVisit && (
          <DeferVisitModal
            visit={deferVisit}
            queue={queue}
            onClose={() => setDeferVisit(null)}
            onConfirm={(afterVisitId) => handleDefer(deferVisit.id, afterVisitId)}
          />
        )}
      </div>
    </div>
  );
};

// ===================== INJECTIONS =====================
const doseLabel = (d) => `${d.medicineName} · Dose ${d.dose} of ${d.of}`;

const InjectionsPanel = ({ data, loading, search, canRecord, recording, onGiven, onStop }) => {
  const q = (search || '').trim().toLowerCase();
  const match = (r) => !q
    || [r.patientName, r.mrn, r.prescriptionNumber].some((v) => v && String(v).toLowerCase().includes(q));
  const toGive = data.toGive.filter(match);
  const given = data.givenToday.filter(match);
  const behind = data.behind.filter(match);
  const waitingDoses = toGive.reduce((n, v) => n + (v.doses || []).length, 0);

  if (loading && !data.toGive.length && !data.givenToday.length && !data.behind.length) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
        <RefreshCw className="h-10 w-10 text-gray-300 mx-auto mb-3 animate-spin" />
        <p className="text-gray-500">Loading injections…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section aria-label="Doses to give" className="bg-white rounded-2xl shadow-xl">
        <header className="px-6 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-gray-900">To give · {waitingDoses}</h2>
          <p className="text-sm text-gray-500">Paid for today at the counter</p>
        </header>
        {toGive.length === 0 ? (
          <p className="px-6 py-8 text-center text-gray-500">
            {data.toGive.length === 0 ? 'No dose is waiting to be given.' : 'No patient matches the search.'}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {toGive.map((v) => (
              <li key={v.invoiceId} className="px-6 py-4 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900">{v.patientName}</p>
                  <p className="text-xs text-gray-500">
                    {v.mrn}{v.paidAt ? ` · paid at ${formatClinicTime(v.paidAt, {}, '')}` : ''}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(v.doses || []).map((d) => (
                      <span key={d.doseId} className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-sm text-emerald-800">
                        <Syringe className="h-4 w-4" aria-hidden="true" /> {doseLabel(d)}
                        {canRecord && v.doses.length > 1 && (
                          <button
                            type="button"
                            onClick={() => onGiven([d.doseId], d.doseId)}
                            disabled={!!recording}
                            className="ml-1 rounded-md border border-emerald-300 bg-white px-2 py-0.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                          >
                            Given
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
                {canRecord && (
                  <button
                    type="button"
                    onClick={() => onGiven(v.doses.map((d) => d.doseId), v.invoiceId)}
                    disabled={!!recording}
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    <Check className="h-4 w-4" aria-hidden="true" /> {v.doses.length > 1 ? 'All given' : 'Given'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Given today" className="bg-white rounded-2xl shadow-xl">
        <header className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">
            Given today · {given.reduce((n, v) => n + (v.doses || []).length, 0)}
          </h2>
        </header>
        {given.length === 0 ? (
          <p className="px-6 py-8 text-center text-gray-500">No dose has been given yet today.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {given.map((v) => (
              <li key={v.invoiceId} className="px-6 py-3">
                <p className="font-semibold text-gray-900">
                  {v.patientName} <span className="text-xs font-normal text-gray-500">{v.mrn}</span>
                </p>
                {(v.doses || []).map((d) => (
                  <p key={d.doseId} className="text-sm text-gray-600">
                    {doseLabel(d)}
                    <span className="text-gray-400">
                      {d.givenAt ? ` · ${formatClinicTime(d.givenAt, {}, '')}` : ''}{d.givenBy ? ` · ${d.givenBy}` : ''}
                    </span>
                  </p>
                ))}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Behind on their course" className="bg-white rounded-2xl shadow-xl">
        <header className="px-6 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-gray-900">Behind on their course · {behind.length}</h2>
          <p className="text-sm text-gray-500">A dose was due and the patient has not come for it</p>
        </header>
        {behind.length === 0 ? (
          <p className="px-6 py-8 text-center text-gray-500">Every course is on schedule.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {behind.map((r) => (
              <li key={r.courseId} className="px-6 py-4 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900">{r.patientName}</p>
                  <p className="text-xs text-gray-500">
                    {[r.mrn, r.prescriptionNumber, r.lastDoseOn && `last dose ${formatClinicDate(r.lastDoseOn, { day: 'numeric', month: 'short' })}`]
                      .filter(Boolean).join(' · ')}
                  </p>
                  {r.phoneNumber && (
                    <p className="mt-1 inline-flex items-center gap-1 text-sm text-gray-700">
                      <Phone className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" /> {r.phoneNumber}
                    </p>
                  )}
                  {(r.progress || []).map((p) => (
                    <p key={p.medicineName} className="text-sm text-gray-600">
                      {p.medicineName}: {p.billed} of {p.of} doses so far, {p.expected} due by yesterday
                    </p>
                  ))}
                </div>
                {canRecord && (
                  <button
                    type="button"
                    onClick={() => onStop(r)}
                    className="rounded-lg border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50"
                  >
                    Stop course
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

// ===================== QUEUE CARD =====================
const NurseQueueCard = ({ visit, onRecordVitals, onCall, onRecall, onDefer, can }) => {
  const allowed = (action) => (typeof can === 'function' ? can(action) : true);
  const patient = visit.patient || {};
  const name = `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || 'Unknown Patient';
  // ageInYears counts by the calendar. Dividing by an average year of 365.25 days showed a
  // patient as 17 on the day they turned 18, depending on where the leap days fell.
  const age = patient.age || patient.dateOfBirth
    ? (patient.age ?? ageInYears(patient.dateOfBirth))
    : null;

  return (
    <div className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all ${visit.isEmergency ? 'border-l-4 border-red-500' : ''}`}>
      <div className="p-5 flex items-center gap-4">
        {/* Token circle */}
        {/* Sized by content rather than a fixed square: a prefixed token ("FB-1") is wider than
            a bare number and would otherwise spill out of the tile. */}
        <div className={`min-w-[4rem] h-16 px-3 bg-gradient-to-br ${visit.isEmergency ? 'from-red-500 to-rose-600' : 'from-emerald-500 to-teal-600'} rounded-2xl flex items-center justify-center shadow-md flex-shrink-0`}>
          <span className="text-xl font-bold text-white whitespace-nowrap">{formatVisitToken(visit)}</span>
        </div>

        {/* Patient info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-lg font-bold text-gray-900">{name}</h3>
            {visit.isEmergency && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                <AlertTriangle className="h-3 w-3" />EMERGENCY
              </span>
            )}
          </div>
          <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
            <span className="flex items-center gap-1"><User className="h-3.5 w-3.5 text-gray-400" />{patient.mrn || 'N/A'}</span>
            {age !== null && <span>{patient.dobEstimated ? 'about ' : ''}{age}y</span>}
            {patient.gender && <span>{toTitleCase(patient.gender)}</span>}
            <span className="flex items-center gap-1">
              <Stethoscope className="h-3.5 w-3.5 text-gray-400" />
              {visit.doctor?.fullName || `Dr. ${visit.doctor?.firstName} ${visit.doctor?.lastName}`}
            </span>
            {visit.createdAt && (
              <span className="flex items-center gap-1 text-gray-400">
                <Clock className="h-3.5 w-3.5" />
                {formatClinicTime(visit.createdAt, { hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>

        {/* Actions — Call and Recall are mutually exclusive: Call only shows
            until the nurse first calls the patient, then it flips to Recall.
            Driven by `visit.nurseCalledAt` from the backend (survives refresh
            and is shared across staff). */}
        <div className="flex-shrink-0 flex items-center gap-2">
          {visit.nurseCalledAt
            ? allowed('CALL_FOR_VITALS') && (
              <button
                onClick={onRecall}
                className="flex items-center gap-1.5 px-3 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl transition-all font-medium text-sm"
                title={`Last called at ${formatClinicTime(visit.nurseCalledAt, { hour: 'numeric', minute: '2-digit' })}`}
              >
                <RotateCcw className="h-4 w-4" />
                <span>Recall</span>
              </button>
            )
            : allowed('CALL_FOR_VITALS') && (
              <button
                onClick={onCall}
                className="flex items-center gap-1.5 px-3 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl transition-all font-medium text-sm"
                title="Call patient for vitals"
              >
                <Volume2 className="h-4 w-4" />
                <span>Call</span>
              </button>
            )}
          {/* Defer — only meaningful once the patient has been called and isn't showing up.
              Hidden until the first call so it doesn't clutter the default flow. */}
          {visit.nurseCalledAt && allowed('DEFER_VISIT') && (
            <button
              onClick={onDefer}
              className="flex items-center gap-1.5 px-3 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl transition-all font-medium text-sm"
              title="Move this patient down the queue"
            >
              <SkipForward className="h-4 w-4" />
              <span>Defer</span>
            </button>
          )}
          {allowed('RECORD_VITALS') && (
            <button
              onClick={onRecordVitals}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2.5 font-medium text-sm transition-all"
            >
              <Activity className="h-4 w-4" />
              <span>Record Vitals</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ===================== VITALS MODAL =====================
// BP is intentionally absent here — it's rendered as a single combined "120/80" field
// outside the grid loop, and parsed into bpSystolic/bpDiastolic at save time so the
// backend schema (separate columns, useful for analytics) doesn't change.
//
// min/max give a sanity range per field — catches typos (negative values, missing
// decimal, an extra zero). They're loose enough not to reject legitimate edge cases
// (e.g. low SpO2 in a critical patient) but tight enough to catch obvious errors.
// =================== AGE-BAND VITALS CONFIGURATION ===================
// Different ages need different vitals captured. BP isn't routinely taken on newborns or
// infants. "Height" doesn't exist for under-2 — it's "Length" (lying down). Pulse is called
// Heart Rate at younger ages because the normal range differs so much. Each band below lists
// only the fields that are clinically relevant + the appropriate validation min/max + a
// normal-range hint so the nurse knows when a reading looks off.

// All possible fields, keyed by the form-state field name. The same `pulse` form field is
// labelled "Heart Rate" for under-5s so we don't need two columns on the backend.
const FIELD_BASE = {
  pulse:            { key: 'pulse',                 unit: 'bpm',   icon: Activity,    color: 'pink' },
  temperature:      { key: 'temperature',           unit: '°F',    icon: Thermometer, color: 'orange' },
  weight:           { key: 'weight',                unit: 'kg',    icon: Weight,      color: 'blue' },
  height:           { key: 'height',                unit: 'cm',    icon: User,        color: 'indigo' },
  length:           { key: 'lengthCm',              unit: 'cm',    icon: User,        color: 'indigo' },
  headCirc:         { key: 'headCircumferenceCm',   unit: 'cm',    icon: User,        color: 'purple' },
  muac:             { key: 'muacCm',                unit: 'cm',    icon: User,        color: 'amber' },
  spo2:             { key: 'spo2',                  unit: '%',     icon: Wind,        color: 'cyan' },
  respiratoryRate:  { key: 'respiratoryRate',       unit: '/min',  icon: Wind,        color: 'teal' },
  bloodSugar:       { key: 'bloodSugar',            unit: 'mg/dL', icon: Droplets,    color: 'purple' },
};

// Per-band: which fields to show, required/optional, label override, validation range, normal-
// range helper text. Pulse is always required (the one universally meaningful vital).
export const BAND_CONFIG = {
  NEWBORN: {
    label: 'Newborn (0–28 days)',
    description: 'BP isn\'t routinely taken at this age - focus on HR, RR, temperature, weight & growth.',
    bp: 'hidden',
    fields: [
      { ...FIELD_BASE.pulse,           label: 'Heart Rate',         required: true,  placeholder: '120–160',  min: 70,  max: 220, normal: '110–160 bpm' },
      { ...FIELD_BASE.respiratoryRate, label: 'Respiratory Rate',   required: true,  placeholder: '40–60',    min: 20,  max: 90,  normal: '40–60/min' },
      { ...FIELD_BASE.temperature,     label: 'Temperature',        required: true,  placeholder: '97–99',    min: 80,  max: 115, normal: '97–99 °F' },
      { ...FIELD_BASE.weight,          label: 'Weight',             required: true,  placeholder: '2.5–4.0',  min: 0.3, max: 8,   normal: '2.5–4.0 kg' },
      { ...FIELD_BASE.length,          label: 'Length',                              placeholder: '45–55',    min: 30,  max: 70,  normal: '45–55 cm' },
      { ...FIELD_BASE.headCirc,        label: 'Head Circumference',                  placeholder: '32–37',    min: 25,  max: 50,  normal: '32–37 cm' },
      { ...FIELD_BASE.spo2,            label: 'SpO₂',                                placeholder: '95–100',   min: 50,  max: 100, normal: '≥ 95%' },
    ],
  },
  INFANT: {
    label: 'Infant (1–12 months)',
    description: 'BP optional at this age. Track length, head circumference and weight for growth.',
    bp: 'hidden',
    fields: [
      { ...FIELD_BASE.pulse,           label: 'Heart Rate',         required: true,  placeholder: '100–160',  min: 60,  max: 220, normal: '100–160 bpm' },
      { ...FIELD_BASE.respiratoryRate, label: 'Respiratory Rate',   required: true,  placeholder: '30–50',    min: 15,  max: 80,  normal: '30–50/min' },
      { ...FIELD_BASE.temperature,     label: 'Temperature',        required: true,  placeholder: '97–99',    min: 80,  max: 115, normal: '97–99 °F' },
      { ...FIELD_BASE.weight,          label: 'Weight',             required: true,  placeholder: '5–10',     min: 1,   max: 15,  normal: '5–10 kg' },
      { ...FIELD_BASE.length,          label: 'Length',                              placeholder: '55–75',    min: 40,  max: 90,  normal: '55–75 cm' },
      { ...FIELD_BASE.headCirc,        label: 'Head Circumference',                  placeholder: '37–45',    min: 30,  max: 55,  normal: '37–45 cm' },
      { ...FIELD_BASE.muac,            label: 'MUAC',                                placeholder: '13–17',    min: 7,   max: 25,  normal: '≥ 12.5 cm' },
      { ...FIELD_BASE.spo2,            label: 'SpO₂',                                placeholder: '95–100',   min: 50,  max: 100, normal: '≥ 95%' },
    ],
  },
  YOUNG_CHILD: {
    label: 'Young child (1–5 years)',
    description: 'MUAC for malnutrition screening. BP optional. Use length for under-2, height once standing.',
    bp: 'optional',
    bpPlaceholder: '90/60',
    fields: [
      { ...FIELD_BASE.pulse,           label: 'Heart Rate',         required: true,  placeholder: '90–140',   min: 50,  max: 200, normal: '90–140 bpm' },
      { ...FIELD_BASE.respiratoryRate, label: 'Respiratory Rate',   required: true,  placeholder: '24–40',    min: 10,  max: 70,  normal: '24–40/min' },
      { ...FIELD_BASE.temperature,     label: 'Temperature',        required: true,  placeholder: '97–99',    min: 80,  max: 115, normal: '97–99 °F' },
      { ...FIELD_BASE.weight,          label: 'Weight',             required: true,  placeholder: '8–20',     min: 3,   max: 35,  normal: '8–20 kg' },
      { ...FIELD_BASE.height,          label: 'Height',                              placeholder: '70–110',   min: 50,  max: 130, normal: '70–110 cm' },
      { ...FIELD_BASE.muac,            label: 'MUAC',               required: true,  placeholder: '14–18',    min: 8,   max: 25,  normal: '≥ 12.5 cm' },
      { ...FIELD_BASE.spo2,            label: 'SpO₂',                                placeholder: '95–100',   min: 50,  max: 100, normal: '≥ 95%' },
    ],
  },
  CHILD: {
    label: 'Child (5–12 years)',
    description: 'Adult-shaped vitals with pediatric normal ranges.',
    bp: 'optional',
    bpPlaceholder: '100/65',
    fields: [
      { ...FIELD_BASE.pulse,           label: 'Pulse',              required: true,  placeholder: '70–110',   min: 40,  max: 180, normal: '70–110 bpm' },
      { ...FIELD_BASE.respiratoryRate, label: 'Respiratory Rate',                    placeholder: '18–30',    min: 8,   max: 60,  normal: '18–30/min' },
      { ...FIELD_BASE.temperature,     label: 'Temperature',                         placeholder: '98.6',     min: 80,  max: 115, normal: '97–99 °F' },
      { ...FIELD_BASE.weight,          label: 'Weight',             required: true,  placeholder: '15–40',    min: 8,   max: 80,  normal: '15–40 kg' },
      { ...FIELD_BASE.height,          label: 'Height',                              placeholder: '110–150',  min: 80,  max: 180, normal: '110–150 cm' },
      { ...FIELD_BASE.spo2,            label: 'SpO₂',                                placeholder: '95–100',   min: 50,  max: 100, normal: '≥ 95%' },
      { ...FIELD_BASE.bloodSugar,      label: 'Blood Sugar',                         placeholder: '70–140',   min: 10,  max: 800 },
    ],
  },
  ADULT: {
    label: 'Adult (18+ years)',
    description: 'Standard adult vitals. BP and Pulse are required.',
    bp: 'required',
    bpPlaceholder: '120/80',
    fields: [
      { ...FIELD_BASE.pulse,           label: 'Pulse',              required: true,  placeholder: '72',       min: 30,  max: 250, normal: '60–100 bpm' },
      { ...FIELD_BASE.temperature,     label: 'Temperature',                         placeholder: '98.6',     min: 80,  max: 115, normal: '97–99 °F' },
      { ...FIELD_BASE.weight,          label: 'Weight',                              placeholder: '70',       min: 20,  max: 300, normal: '' },
      { ...FIELD_BASE.height,          label: 'Height',                              placeholder: '170',      min: 100, max: 230, normal: '' },
      { ...FIELD_BASE.bloodSugar,      label: 'Blood Sugar',                         placeholder: '100',      min: 10,  max: 1000, normal: '70–140 mg/dL' },
      { ...FIELD_BASE.spo2,            label: 'SpO₂',                                placeholder: '98',       min: 30,  max: 100, normal: '≥ 95%' },
      { ...FIELD_BASE.respiratoryRate, label: 'Respiratory Rate',                    placeholder: '16',       min: 5,   max: 50,  normal: '12–20/min' },
    ],
  },
};
// 12-18 year olds have a band of their own in the shared age engine (utils/vitals): adult ranges,
// labelled adolescent so the reading does not present as "adult" beside the Minor badge. It was
// missing here, so opening Record Vitals for any 12-17 year old read band.description off undefined
// and the dialog crashed. The form and its rules are the adult ones.
BAND_CONFIG.ADOLESCENT = {
  ...BAND_CONFIG.ADULT,
  label: 'Adolescent (12–18 years)',
  description: 'Adult vitals and normal ranges, for 12–18 year olds. BP and Pulse are required.',
};

export const BAND_OPTIONS = [
  { value: 'NEWBORN',     label: 'Newborn (0–28 days)' },
  { value: 'INFANT',      label: 'Infant (1–12 months)' },
  { value: 'YOUNG_CHILD', label: 'Young child (1–5 yrs)' },
  { value: 'CHILD',       label: 'Child (5–12 yrs)' },
  { value: 'ADOLESCENT',  label: 'Adolescent (12–18 yrs)' },
  { value: 'ADULT',       label: 'Adult (18+ yrs)' },
];

// Compute the age band purely from DOB. The nurse can manually override later if the DOB is
// wrong or missing — but defaulting from DOB makes the common case zero-click.
// Age band comes from the shared engine in utils/vitals. A local copy used to live here and
// had drifted: it never gained the ADOLESCENT band, so a 12-17 year old was judged against
// ADULT ranges here while the prescription vitals panel judged them as ADOLESCENT.
const getAgeBandFromDob = (dob) => getAgeBandKey(dob);

// All form-state keys we may touch — used to initialise form state regardless of which band
// is active, so swapping bands doesn't drop values the user already typed.
const ALL_VITAL_KEYS = [
  'pulse', 'temperature', 'weight', 'height', 'bloodSugar', 'spo2',
  'respiratoryRate', 'lengthCm', 'headCircumferenceCm', 'muacCm',
];

// Numeric normal ranges per age band — used for the pre-save abnormality check (warning
// dialog). These mirror the helper text in BAND_CONFIG (e.g. "Normal: 60–100 bpm") but in
// machine-readable form so we can compare against captured values.
const BAND_NORMAL = {
  NEWBORN:     { pulse: [110, 160], respiratoryRate: [40, 60], temperature: [97, 99], spo2: [95, 100] },
  INFANT:      { pulse: [100, 160], respiratoryRate: [30, 50], temperature: [97, 99], spo2: [95, 100] },
  YOUNG_CHILD: { pulse: [90, 140],  respiratoryRate: [24, 40], temperature: [97, 99], spo2: [95, 100] },
  CHILD:       { pulse: [70, 110],  respiratoryRate: [18, 30], temperature: [97, 99], spo2: [95, 100] },
  ADOLESCENT:  { pulse: [60, 100],  respiratoryRate: [12, 20], temperature: [97, 99], spo2: [95, 100] },
  ADULT:       { pulse: [60, 100],  respiratoryRate: [12, 20], temperature: [97, 99], spo2: [95, 100] },
};

// Find abnormal readings in the captured form. Returns an array of human-readable issue
// lines used by the pre-save confirmation dialog. Mirrors the prescription-side assessment
// so the doctor and the nurse see consistent flagging.
const assessNurseVitals = (form, bandKey, bpSystolic, bpDiastolic) => {
  const normal = BAND_NORMAL[bandKey] || BAND_NORMAL.ADULT;
  const issues = [];

  const check = (value, range, label, unit) => {
    if (value == null || value === '') return;
    const num = Number(value);
    if (!Number.isFinite(num) || !range) return;
    const [low, high] = range;
    if (num < low || num > high) {
      issues.push(`${label} ${num} ${unit} - ${num < low ? 'low' : 'high'} (normal ${low}–${high} ${unit})`);
    }
  };

  check(form.pulse,           normal.pulse,           'Pulse / HR',     'bpm');
  check(form.respiratoryRate, normal.respiratoryRate, 'Resp Rate',      '/min');
  check(form.spo2,            normal.spo2,            'SpO₂',           '%');

  // Temperature — flag fever, hypothermia, slightly elevated separately for clarity.
  const t = Number(form.temperature);
  if (Number.isFinite(t) && t > 0) {
    if (t >= 103)      issues.push(`Temperature ${t} °F - high fever`);
    else if (t >= 100.4) issues.push(`Temperature ${t} °F - fever`);
    else if (t < 95)   issues.push(`Temperature ${t} °F - hypothermia`);
    else if (t < 96.8) issues.push(`Temperature ${t} °F - low`);
    else if (t > 99)   issues.push(`Temperature ${t} °F - slightly elevated`);
  }

  // BP — only flag for older children / adults where it's clinically meaningful.
  if ((bandKey === 'CHILD' || bandKey === 'ADOLESCENT' || bandKey === 'ADULT') && bpSystolic != null && bpDiastolic != null) {
    if (bpSystolic >= 180 || bpDiastolic >= 120) issues.push(`BP ${bpSystolic}/${bpDiastolic} mmHg - hypertensive crisis`);
    else if (bpSystolic >= 140 || bpDiastolic >= 90) issues.push(`BP ${bpSystolic}/${bpDiastolic} mmHg - high`);
    else if (bpSystolic < 90 || bpDiastolic < 60) issues.push(`BP ${bpSystolic}/${bpDiastolic} mmHg - low`);
  }

  // MUAC — WHO under-5 malnutrition thresholds.
  if ((bandKey === 'INFANT' || bandKey === 'YOUNG_CHILD') && form.muacCm != null && form.muacCm !== '') {
    const muac = Number(form.muacCm);
    if (Number.isFinite(muac)) {
      if (muac < 11.5)      issues.push(`MUAC ${muac} cm - severe acute malnutrition (WHO)`);
      else if (muac < 12.5) issues.push(`MUAC ${muac} cm - moderate acute malnutrition`);
    }
  }

  // Blood sugar — broad flags, only clearly pathological values.
  const bs = Number(form.bloodSugar);
  if (Number.isFinite(bs) && bs > 0) {
    if (bs < 54)       issues.push(`Blood sugar ${bs} mg/dL - severe hypoglycemia`);
    else if (bs < 70)  issues.push(`Blood sugar ${bs} mg/dL - hypoglycemia`);
    else if (bs > 300) issues.push(`Blood sugar ${bs} mg/dL - severe hyperglycemia`);
    else if (bs > 200) issues.push(`Blood sugar ${bs} mg/dL - hyperglycemia`);
  }

  return issues;
};

// Filter input to digits + at most one decimal point. Strips negatives, letters,
// and anything else a `type="text"` field could absorb. Returns the cleaned string.
const sanitizeNumericInput = (raw) => {
  let s = String(raw || '').replace(/[^\d.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  return s;
};

// ===================== RECORDED VITALS CARD =====================
// Compact summary of a visit whose vitals were recorded today. The nurse can click
// "Edit Vitals" to reopen the same VitalsModal pre-populated with the existing values
// so missing fields can be backfilled or wrong ones corrected.
const RecordedVitalsCard = ({ visit, onEdit }) => {
  const patient = visit.patient || {};
  const name = `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || 'Unknown Patient';

  // Pick which vitals to show based on the patient's age band — same field rules as the
  // Vitals modal, so the card stays consistent with what the nurse was asked to capture.
  // For under-2: Length replaces Height. Under-5: MUAC + Head Circ added; BP hidden for
  // newborns/infants since they're not routinely measured at that age.
  const band = getAgeBandFromDob(patient.dateOfBirth);
  const isUnderTwo = band === 'NEWBORN' || band === 'INFANT';
  const showBP = band === 'CHILD' || band === 'ADULT';
  const showHeadCirc = isUnderTwo || band === 'YOUNG_CHILD';
  const showMuac = band === 'INFANT' || band === 'YOUNG_CHILD';

  const heightLabel = isUnderTwo ? 'Length' : 'Height';
  const heightValue = isUnderTwo
    ? (visit.lengthCm ?? visit.height)
    : (visit.height ?? visit.lengthCm);

  const vitalsList = [
    showBP && { label: 'BP',     value: (visit.bpSystolic && visit.bpDiastolic) ? `${visit.bpSystolic}/${visit.bpDiastolic}` : null, unit: 'mmHg' },
    { label: isUnderTwo || band === 'YOUNG_CHILD' ? 'HR' : 'Pulse', value: visit.pulse,            unit: 'BPM' },
    { label: 'Temp',   value: visit.temperature,      unit: '°F' },
    { label: 'Weight', value: visit.weight,           unit: 'KG' },
    { label: heightLabel, value: heightValue,         unit: 'CM' },
    showHeadCirc && { label: 'Head',   value: visit.headCircumferenceCm, unit: 'CM' },
    showMuac    && { label: 'MUAC',   value: visit.muacCm,              unit: 'CM' },
    !isUnderTwo && { label: 'Sugar',  value: visit.bloodSugar,       unit: 'mg/dL' },
    { label: 'SpO2',   value: visit.spo2,             unit: '%' },
    { label: 'Resp',   value: visit.respiratoryRate,  unit: '/min' },
  ].filter(Boolean);
  const recordedCount = vitalsList.filter(v => v.value !== null && v.value !== undefined && v.value !== '').length;
  const totalCount = vitalsList.length;
  const hasMissing = recordedCount < totalCount;

  return (
    <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all">
      <div className="p-5">
        <div className="flex items-center gap-4 mb-3">
          <div className="min-w-[3.5rem] h-14 px-2.5 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center shadow-md flex-shrink-0">
            <span className="text-lg font-bold text-white whitespace-nowrap">{formatVisitToken(visit)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-bold text-gray-900">{name}</h3>
              {hasMissing && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                  {recordedCount}/{totalCount} fields
                </span>
              )}
            </div>
            <div className="flex items-center flex-wrap gap-x-4 gap-y-0.5 text-sm text-gray-600 mt-0.5">
              <span className="flex items-center gap-1"><User className="h-3.5 w-3.5 text-gray-400" />{patient.mrn || 'N/A'}</span>
              <span className="flex items-center gap-1">
                <Stethoscope className="h-3.5 w-3.5 text-gray-400" />
                {visit.doctor?.fullName || `Dr. ${visit.doctor?.firstName || ''} ${visit.doctor?.lastName || ''}`.trim()}
              </span>
              {visit.vitalsRecordedAt && (
                <span className="flex items-center gap-1 text-gray-400">
                  <Clock className="h-3.5 w-3.5" />
                  Recorded {formatClinicTime(visit.vitalsRecordedAt, { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onEdit}
            className="flex-shrink-0 flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2.5 font-medium text-sm transition-all"
            title="Edit / amend vitals"
          >
            <Edit2 className="h-4 w-4" />
            <span>Edit Vitals</span>
          </button>
        </div>
        {/* Vitals strip — recorded values are bold, missing ones are muted "—". Uses flex-wrap
            since the field set is age-band-dependent and counts vary from 6 to 10 cells. */}
        <div className="flex flex-wrap gap-2 pl-[72px]">
          {vitalsList.map(v => {
            const present = v.value !== null && v.value !== undefined && v.value !== '';
            return (
              <div
                key={v.label}
                className={`text-center px-3 py-1.5 rounded-lg min-w-[64px] ${present ? 'bg-emerald-50' : 'bg-gray-50'}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{v.label}</div>
                <div className={`text-sm font-bold ${present ? 'text-gray-900' : 'text-gray-300'}`}>
                  {present ? v.value : '—'}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};


// Parse "120/80" → { ok: true, systolic, diastolic } or { ok: false, reason }.
// Returning a reason lets the UI tell the nurse exactly which failure mode they hit
// (format vs range vs values-swapped) so they don't stare at a generic "invalid".
const parseBpInput = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return { ok: false, reason: 'Required - e.g. 120/80' };
  const m = s.match(/^(\d{2,3})\s*\/\s*(\d{2,3})$/);
  if (!m) return { ok: false, reason: 'Use format like 120/80 (digits separated by /)' };
  const systolic = Number(m[1]);
  const diastolic = Number(m[2]);
  if (systolic < 30 || systolic > 300) return { ok: false, reason: 'Systolic out of range (expected 30–300)' };
  if (diastolic < 20 || diastolic > 250) return { ok: false, reason: 'Diastolic out of range (expected 20–250)' };
  if (diastolic >= systolic) {
    return {
      ok: false,
      reason: `Looks swapped - systolic must be the larger number. Did you mean ${diastolic}/${systolic}?`,
    };
  }
  return { ok: true, systolic, diastolic };
};

// Exported for re-use: when the clinic has nurse.operational turned off, PrescriptionManagement
// opens the same modal at Rx start so the doctor records vitals before the prescription wizard.
export const VitalsModal = ({ visit, recordedBy, onClose, onSaved }) => {
  const notify = useToast();
  const confirm = useConfirm();
  const patient = visit.patient || {};
  const name = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();

  // Default the band from DOB but let the nurse override via the dropdown in the header
  // (rare, but useful when DOB is wrong or missing).
  const [bandKey, setBandKey] = useState(() => getAgeBandFromDob(patient.dateOfBirth));
  const band = BAND_CONFIG[bandKey];

  const [form, setForm] = useState(() => {
    // Pre-fill ALL keys (not just current-band ones) so that switching bands doesn't drop
    // values the user already entered for a field that's still relevant.
    const initial = {};
    ALL_VITAL_KEYS.forEach(k => { initial[k] = visit[k] ?? ''; });
    initial.bp = (visit.bpSystolic && visit.bpDiastolic)
      ? `${visit.bpSystolic}/${visit.bpDiastolic}`
      : '';
    return initial;
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const vitalsDialogRef = useDialog(() => { if (!saving) onClose(); });

  const setValue = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
  };

  // Live BMI: weight (kg) / (height_m)^2. For under-2 we use Length in place of Height — same
  // physical measurement, just recorded lying down. BMI itself is less clinically meaningful
  // under 5, but the user asked to show it whenever both inputs are present; we add a small
  // disclaimer below 5 instead of hiding it.
  const bmi = computeBmi({ weight: form.weight, height: form.height, lengthCm: form.lengthCm });
  // WHO BMI categories are defined for ADULTS. They are not valid for anyone under 18: a child's
  // BMI has to be read against a BMI-for-age chart, where the healthy range moves with age and sex,
  // so a fixed 18.5/25/30 scale can label a perfectly normal 7-year-old "Underweight". The number
  // is still shown at every age - only the category is withheld, with a note saying why.
  const interpretBmi = (b) => {
    if (b == null) return null;
    if (b < 18.5) return { label: 'Underweight', color: 'bg-amber-100 text-amber-700' };
    if (b < 25)   return { label: 'Normal',      color: 'bg-emerald-100 text-emerald-700' };
    if (b < 30)   return { label: 'Overweight',  color: 'bg-orange-100 text-orange-700' };
    return        { label: 'Obese',        color: 'bg-red-100 text-red-700' };
  };
  const bmiCategory = bandKey === 'ADULT' ? interpretBmi(bmi) : null;
  // Why the category is missing depends on the age: under 5 BMI itself is a poor measure, while
  // from 5 to 18 the measure is fine but the adult thresholds are not.
  const bmiNote =
    bandKey === 'NEWBORN' || bandKey === 'INFANT' || bandKey === 'YOUNG_CHILD'
      ? 'BMI is less reliable under 5 - use weight-for-age / WHO charts instead.'
      : 'Adult BMI categories do not apply under 18 - read against a BMI-for-age chart.';

  // A visit that already carries a recording timestamp is being AMENDED, not recorded. By now the
  // doctor may already have consulted on the strength of the original reading, so the change needs
  // a stated reason: it is audited and surfaces in Reports → Vitals Edits.
  const isEdit = !!visit.vitalsRecordedAt;
  const [editReason, setEditReason] = useState('');
  const [reasonError, setReasonError] = useState('');

  const handleSave = async () => {
    const errs = {};

    // BP handling depends on band: 'hidden' = skip, 'optional' = validate format if present,
    // 'required' = must be present + valid.
    let bpResult = null;
    if (band.bp !== 'hidden') {
      const raw = (form.bp || '').trim();
      if (!raw) {
        if (band.bp === 'required') errs.bp = 'Required - e.g. 120/80';
      } else {
        bpResult = parseBpInput(raw);
        if (!bpResult.ok) errs.bp = bpResult.reason;
      }
    }

    // Validate each band field. Skip empty optional ones; required must be present. We only
    // enforce basic sanity (number, non-negative) — clinical out-of-range readings are
    // surfaced through the abnormality dialog below instead of hard-blocking the save. This
    // lets the nurse record legitimate extreme values (e.g. severe pediatric obesity, fever)
    // without fighting the form. The per-field `min`/`max` in BAND_CONFIG are kept as
    // documentation but intentionally not enforced here.
    band.fields.forEach(f => {
      const raw = String(form[f.key] ?? '').trim();
      if (!raw) {
        if (f.required) errs[f.key] = 'Required';
        return;
      }
      const num = Number(raw);
      if (!Number.isFinite(num)) {
        errs[f.key] = 'Must be a number';
      } else if (num < 0) {
        errs[f.key] = 'Cannot be negative';
      }
    });
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // Abnormality double-check — same logic the doctor sees on the prescription readonly
    // panel. If any captured reading falls outside its age-band normal range, prompt the
    // nurse to confirm "review" vs "save anyway" before we commit. Catches typos
    // (e.g. weight 100000 kg, temp 108 °F) that pass field-level validation but are clearly
    // wrong clinically.
    const issues = assessNurseVitals(
      form,
      bandKey,
      bpResult?.ok ? bpResult.systolic : null,
      bpResult?.ok ? bpResult.diastolic : null,
    );
    if (issues.length > 0) {
      const ok = await confirm({
        title: `${issues.length} reading${issues.length === 1 ? '' : 's'} look${issues.length === 1 ? 's' : ''} abnormal`,
        message: (
          <div>
            <p className="mb-2">Please double-check before saving:</p>
            <ul className="list-disc pl-5 space-y-0.5 text-sm">
              {issues.map((line, i) => <li key={i}>{line}</li>)}
            </ul>
            <p className="mt-3 text-xs text-gray-500">Pick "Review" to go back and correct values, or "Save anyway" if these are intentional.</p>
          </div>
        ),
        confirmText: 'Save anyway',
        cancelText: 'Review',
        tone: 'warning',
      });
      if (!ok) return; // nurse chose Review — stay on the modal
    }

    // Build payload. Only include values for fields shown in the current band — if the band
    // doesn't show a field, we send null/skip so we don't accidentally persist a stale value
    // the user typed and then switched bands away from.
    const payload = {};
    if (bpResult && bpResult.ok) {
      payload.bpSystolic = bpResult.systolic;
      payload.bpDiastolic = bpResult.diastolic;
    }
    band.fields.forEach(f => {
      const v = form[f.key];
      if (v !== '' && v !== null && v !== undefined) {
        const num = Number(v);
        if (!isNaN(num)) payload[f.key] = num;
      }
    });

    if (isEdit) {
      const reason = editReason.trim();
      if (!reason) {
        setReasonError('Please say why these vitals are being changed');
        return;
      }
      payload.editReason = reason;
    }

    setSaving(true);
    try {
      const updated = await visitAPI.recordVitals(visit.id, payload, recordedBy);
      onSaved(updated);
    } catch (err) {
      console.error('Failed to save vitals:', err);
      notify('error', 'Failed to save vitals: ' + (err.message || 'Please try again'));
    } finally { setSaving(false); }
  };

  return (
    <div
      ref={vitalsDialogRef}
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-6 text-white flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold">Record Vitals</h2>
              <p className="text-white/80 mt-1">
                Token <span className="font-bold">{formatVisitToken(visit)}</span> · {name}
                {patient.mrn && <> · <span className="font-mono text-sm">{patient.mrn}</span></>}
              </p>
            </div>
            <button aria-label="Close" onClick={onClose} disabled={saving} className="p-2 hover:bg-white/20 rounded-full disabled:opacity-50">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Age band — read-only when DOB is on file. Letting the nurse arbitrarily pick a
              band would let her record adult-shaped vitals on a newborn (or vice versa) and
              silently defeat the per-band field set + validation. If the DOB is wrong, the
              fix is to correct it on the patient record, not override here. We only fall
              back to a dropdown when DOB is genuinely missing — the safety-net case. */}
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <label className="text-xs uppercase tracking-wider text-white/70 font-semibold">Age band</label>
            {patient.dateOfBirth ? (
              <>
                <span className="inline-flex items-center gap-2 bg-white/15 border border-white/30 text-white rounded-lg px-3 py-1.5 text-sm font-semibold">
                  {(BAND_OPTIONS.find(o => o.value === bandKey) || BAND_OPTIONS[BAND_OPTIONS.length - 1]).label}
                </span>
                <span className="text-xs text-white/70">Auto-detected from DOB</span>
              </>
            ) : (
              <>
                <Select
                  value={bandKey}
                  onChange={setBandKey}
                  options={BAND_OPTIONS}
                  className="w-44"
                  buttonClassName="px-3 py-1.5 font-semibold"
                />
                <span className="text-xs text-amber-200">DOB not on file · please pick the band manually</span>
              </>
            )}
          </div>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-6">
          <p className="text-sm text-gray-600 mb-4">{band.description}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {/* Combined BP field — entered as "120/80", parsed at save time. Hidden entirely
                for newborns and infants; shown as optional or required depending on the band.
                Spans 2 cols on md+ to keep its visual weight when it's there. */}
            {band.bp !== 'hidden' && (() => {
              const hasError = !!errors.bp;
              const bpRequired = band.bp === 'required';
              return (
                <div className={`md:col-span-2 rounded-xl p-4 border-2 transition-colors ${
                  hasError ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <Heart className="w-4 h-4 text-red-500" />
                    <label className="text-sm font-medium text-gray-700">
                      Blood Pressure
                      {bpRequired && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.bp}
                      onChange={(e) => setValue('bp', e.target.value)}
                      placeholder={band.bpPlaceholder || '120/80'}
                      className="w-full pl-3 pr-16 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-lg font-semibold bg-white tracking-wider"
                      autoFocus={bpRequired}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium pointer-events-none whitespace-nowrap">
                      mmHg
                    </span>
                  </div>
                  {hasError
                    ? <p className="text-red-500 text-xs mt-1">{errors.bp}</p>
                    : <p className="text-gray-400 text-xs mt-1">Systolic / Diastolic</p>}
                </div>
              );
            })()}
            {band.fields.map((field) => {
              const hasError = !!errors[field.key];
              return (
                <div
                  key={field.key}
                  className={`rounded-xl p-4 border-2 transition-colors ${
                    hasError ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <field.icon className={`w-4 h-4 text-${field.color}-500`} />
                    <label className="text-sm font-medium text-gray-700">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={form[field.key]}
                      onChange={(e) => setValue(field.key, sanitizeNumericInput(e.target.value))}
                      placeholder={field.placeholder}
                      className="w-full pl-3 pr-14 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-lg font-semibold bg-white"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium pointer-events-none whitespace-nowrap">
                      {field.unit}
                    </span>
                  </div>
                  {hasError
                    ? <p className="text-red-500 text-xs mt-1">{errors[field.key]}</p>
                    : field.normal && <p className="text-gray-400 text-xs mt-1">Normal: {field.normal}</p>}
                </div>
              );
            })}
          </div>

          {/* Amendment reason — only when overwriting a reading that was already committed.
              Placed under the fields rather than at the top so it reads as the last thing you
              confirm before saving, and it is the only field on this form the backend refuses
              the save without. */}
          {isEdit && (
            <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <label className="block text-sm font-semibold text-amber-900">
                Reason for changing these vitals <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-amber-800/80 mt-0.5 mb-2">
                Recorded {formatClinicTime(visit.vitalsRecordedAt, { hour: 'numeric', minute: '2-digit' })}
                {visit.vitalsRecordedBy?.fullName ? ` by ${visit.vitalsRecordedBy.fullName}` : ''}.
                This change is logged against the visit and appears in Reports.
              </p>
              <input
                type="text"
                value={editReason}
                onChange={(e) => { setEditReason(e.target.value); if (reasonError) setReasonError(''); }}
                placeholder="e.g. BP re-checked on the other arm, first reading was mis-typed"
                maxLength={500}
                className={`w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-500 ${
                  reasonError ? 'border-red-400' : 'border-amber-300'
                }`}
              />
              {reasonError && <p className="text-red-600 text-xs mt-1">{reasonError}</p>}
            </div>
          )}

          {/* Live BMI chip — appears as soon as both weight and height/length are present.
              For under-5 we suppress the WHO category badge (BMI isn't the right metric at
              that age) but still show the number for reference. */}
          {bmi !== null && (
            <div className="mt-5 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                  <Activity className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700">BMI</div>
                  <div className="text-2xl font-bold text-gray-900">{bmi.toFixed(1)}</div>
                </div>
              </div>
              <div className="text-right">
                {bmiCategory ? (
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${bmiCategory.color}`}>
                    {bmiCategory.label}
                  </span>
                ) : (
                  <span className="text-xs text-gray-500 italic">{bmiNote}</span>
                )}
                <div className="text-[10px] text-gray-500 mt-1">
                  kg/m² · derived from weight + {form.height ? 'height' : 'length'}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-200 p-4 flex justify-end gap-3 bg-gray-50">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2.5 text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-100 font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2.5 font-medium flex items-center gap-2 disabled:opacity-60"
          >
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            <span>{saving ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default NurseStation;
